import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdJml, FeatureFlagIdRoleMiningSimilarityUx } from 'c1gen/features'
import { palette } from 'c1ui'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CopilotIcon } from 'components/core/icons/copilot'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { useFeatureChecker } from 'components/no-ui/capability'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { appEntitlementIdParts } from 'components/pages/entitlement/helpers'
import { useCatalog } from 'components/pages/requestcatalog/details/context/CatalogContext'
import { Info, KeyRound } from 'lucide-react'
import { AppEntitlementCorrelation, SearchAppEntitlementCorrelationForAccessProfileRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { searchAppEntitlementCorrelationForAccessProfileApi } from 'redux_toolkit/entitlements'
import { AppEntitlementRoleMiningSearchFilter } from './AppEntitlementRoleMiningSearchFilter'
import { EntitlementListStyles } from './styles'

const ADD_THRESHOLD = 80
const CONSIDER_ADDING_THRESHOLD = 50

const Recommendation: React.FC<{ distance: number }> = ({ distance }) => {
    const similarity = Math.round((1.0 - distance) * 100)

    return (
        <C1Chip
            label={
                similarity > ADD_THRESHOLD ? (
                    <FormattedMessage
                        id="components_catalog_entitlement_correlation_recommendation_add"
                        defaultMessage="Add"
                    />
                ) : similarity > CONSIDER_ADDING_THRESHOLD ? (
                    <FormattedMessage
                        id="components_catalog_entitlement_correlation_recommendation_consider_adding"
                        defaultMessage="Consider adding"
                    />
                ) : (
                    <FormattedMessage
                        id="components_catalog_entitlement_correlation_recommendation_not_recommended"
                        defaultMessage="Not recommended"
                    />
                )
            }
            color={similarity > ADD_THRESHOLD ? 'success' : similarity > CONSIDER_ADDING_THRESHOLD ? 'primary' : 'info'}
        />
    )
}

const cols: Array<C1TableReduxColumn<AppEntitlementCorrelation>> = [
    {
        field: 'entitlement',
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_correlation_table_entitlement"
                defaultMessage="Entitlement"
            />
        ),
        Component: ({ row }: { row: AppEntitlementCorrelation }): React.ReactElement => <Box>
            <C1EntitlementDisplayNameWithFetch
                entitlementId={row.appEntitlement?.id || ''}
                appId={row.appEntitlement?.appId || ''}
                showTooltip
            />
        </Box>,
    },
]

const colsWithRoleMining: Array<C1TableReduxColumn<AppEntitlementCorrelation>> = [
    {
        field: 'entitlement',
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_correlation_table_entitlement"
                defaultMessage="Entitlement"
            />
        ),
        maxWidth: '32em',
        Component: ({ row }: { row: AppEntitlementCorrelation }): React.ReactElement => <Box>
            <C1EntitlementDisplayNameWithFetch
                entitlementId={row.appEntitlement?.id || ''}
                appId={row.appEntitlement?.appId || ''}
                showTooltip
            />
        </Box>,
    },
    {
        field: 'distance',
        label: (
            <Stack direction="row" spacing={1}>
                <Box sx={{ pr: 1 }}><CopilotIcon /></Box>
                <FormattedMessage
                    id="components_catalog_entitlement_correlation_table_recommendation"
                    defaultMessage="Recommendation"
                />
                <Tooltip title={
                    <FormattedMessage
                        id="components_catalog_entitlement_correlation_recommendation_tooltip"
                        defaultMessage="We recommend adding entitlements with {add_threshold}% or greater confidence. Entitlements above {consider_adding_threshold}% are marked “consider adding”."
                        values={{
                            add_threshold: ADD_THRESHOLD,
                            consider_adding_threshold: CONSIDER_ADDING_THRESHOLD,
                        }}
                    />
                }
                >
                    <C1Icon icon={Info} sx={{ color: palette.text.secondary }} />
                </Tooltip>
            </Stack>
        ),
        Component: ({ row }): React.ReactElement => (row.distance !== undefined ?
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Recommendation distance={row.distance} />
            </Box>
            : <C1EmptyIndicator />),
    },
    {
        field: 'confidence',
        label: (
            <Stack direction="row" spacing={1}>
                <Box sx={{ pr: 1 }}>
                    <FormattedMessage
                        id="components_catalog_entitlement_correlation_table_confidence"
                        defaultMessage="Confidence"
                    />
                </Box>
                <Tooltip title={
                    <FormattedMessage
                        id="components_catalog_entitlement_correlation_confidence_tooltip"
                        defaultMessage="A higher confidence means this entitlement is a great fit for this access profile, given the profile’s current assignees."
                    />
                }
                >
                    <C1Icon icon={Info} sx={{ color: palette.text.secondary }} />
                </Tooltip>
            </Stack>
        ),
        Component: ({ row }): React.ReactElement => (row.distance !== undefined ? <Typography>
            <FormattedNumber value={(1 - row.distance) * 100} maximumFractionDigits={0} />
            %
        </Typography> : <C1EmptyIndicator />),
    },
]
interface Props {
    setSelectedEntitlements: (refs: AppEntitlementCorrelation[]) => void
    selectedEntitlements?: AppEntitlementCorrelation[]
}

export const AppEntitlementCorrelationForAccessProfileList = ({
    setSelectedEntitlements,
    selectedEntitlements,
}: Props): React.ReactElement => {
    const { requestCatalogId, memberCount } = useCatalog()
    const [filter, setFilter] = useState<SearchAppEntitlementCorrelationForAccessProfileRequest>(
        SearchAppEntitlementCorrelationForAccessProfileRequest.create({
            accessProfileId: requestCatalogId,
        }),
    )
    const hasRoleMiningFF = useFeatureChecker(FeatureFlagIdRoleMiningSimilarityUx)
    const hasJmlFF = useFeatureChecker(FeatureFlagIdJml)

    const defaultPageSize = 100

    const {
        data: appEntitlementCorrelations = [],
        isLoading,
        isError,
        getPage,
        canLoadMore,
        error: listEntitlementsError,
    } = searchAppEntitlementCorrelationForAccessProfileApi
        .useSearchAppEntitlementCorrelationForAccessProfile(filter, { pageSize: defaultPageSize })

    const [page, setPage] = useState<number>(0)

    const onPageChange = useCallback(async (n: number) => {
        await getPage(n)
        setPage(n)
    }, [getPage])

    const search = useMemo(() => <Stack direction="row" spacing={2}>
        <AppEntitlementRoleMiningSearchFilter initialState={filter} onChange={setFilter} />
    </Stack>, [filter])

    const table = <C1TableRedux<AppEntitlementCorrelation>
        cols={hasRoleMiningFF && hasJmlFF && (!!memberCount && memberCount > 0) ? colsWithRoleMining : cols}
        data={appEntitlementCorrelations}
        isError={isError}
        isLoading={isLoading}
        onPageChange={onPageChange}
        error={listEntitlementsError}
        page={page}
        pageSize={defaultPageSize}
        canLoadMore={canLoadMore}
        getId={(row): string => appEntitlementIdParts(row.appEntitlement?.appId || '', row.appEntitlement?.id || '')}
        emptyState={<EmptyList
            text={<FormattedMessage id="components_request_catalog_empty_no_entitlements_found" defaultMessage="No entries found." />}
            icon={<C1Icon icon={KeyRound} />}
        />}
        selections={selectedEntitlements}
        setSelections={setSelectedEntitlements}
    />

    return (
        <Box sx={EntitlementListStyles.container}>
            <List<AppEntitlementCorrelation>
                table={table}
                search={search}
                titleVariant="h5"
            />
        </Box>
    )
}
